import {
  FlaskConical, Search, Network, Layers, Target, Brain, Globe,
  Sprout, FlaskRound, GraduationCap, Factory, CheckCircle,
} from 'lucide-react';
import Reveal from '../components/animations/Reveal';

const AboutPage = () => (
  <div className="py-16 space-y-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">About <span className="text-brand">CryMiner</span></h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            CryMiner is a cutting-edge bioinformatics platform built to identify, annotate, classify, and analyze
            Cry (crystal) toxin genes from bacterial genomes. By integrating genomics, bioinformatics, and artificial
            intelligence, it unlocks the hidden potential of bacterial genomes to drive the next generation of
            sustainable pest-control technologies — empowering researchers and innovators in agricultural biotechnology.
          </p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-12 mb-16 items-center max-w-4xl mx-auto">
        <Reveal delay={100}>
          <div className="space-y-6">
            <h3 className="font-display text-2xl font-semibold text-brand mb-4">Scientific Foundation</h3>
            <p className="text-slate-600 leading-relaxed">
              Bacillus thuringiensis is a natural pathogen that produces three-domain Cry toxins,
              highly specific proteins offering an eco-friendly and cost-effective solution for insect
              control in agriculture.
            </p>
            <p className="text-slate-600 leading-relaxed">
              The widespread use of Bt toxins has revolutionized insect pest control, significantly
              reducing crop losses while minimizing environmental impact.
            </p>
          </div>
        </Reveal>
        <Reveal delay={220}>
          <div className="relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:-translate-y-1 hover:border-brand hover:shadow-lg transition-all duration-300 group">
            <FlaskConical className="w-12 h-12 text-brand mb-4 group-hover:rotate-12 group-hover:scale-110 transition-transform relative z-10" />
            <h4 className="font-display text-xl font-semibold text-slate-900 mb-3 relative z-10">Research Impact</h4>
            <p className="text-slate-600 relative z-10">
              Our tool enables researchers to accurately identify and retrieve Cry gene data,
              supporting innovation in biopesticide development and resistance management strategies.
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <h3 className="font-display text-3xl font-bold text-slate-900 text-center mb-10">Key Features</h3>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[
          { icon: Search, title: 'Comprehensive Cry Gene Identification', description: 'Rapid, accurate detection of Cry toxin genes from complete genomes, draft assemblies, metagenomic datasets, and whole-genome sequencing data.' },
          { icon: Network, title: 'Automated Annotation & Classification', description: 'Cry family identification, toxin subtype classification, domain architecture analysis, functional annotation, and evolutionary relationship mapping.' },
          { icon: Layers, title: 'Comparative Genomic Analysis', description: 'Compare Cry repertoires across strains and species to identify novel variants, assess diversity, and understand evolutionary dynamics.' },
          { icon: Target, title: 'Insect Target Prediction', description: 'Predict likely insect targets from sequence similarity, domain composition, structural motifs, and known toxin databases.' },
          { icon: Brain, title: 'AI-Powered Novel Toxin Discovery', description: 'Advanced algorithms surface uncharacterized Cry proteins, candidate insecticidal genes, and synthetic-biology opportunities.' },
          { icon: Globe, title: 'Interactive Visualization Tools', description: 'Genome maps, phylogenetic trees, domain architecture diagrams, comparative toxin profiles, and evolutionary networks.' },
        ].map((feature, index) => {
          const Icon = feature.icon;
          return (
            <Reveal key={index} delay={index * 90}>
              <div className="h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-2 hover:shadow-lg transition-all duration-300 group">
                <div className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-brand-tint mb-4">
                  <Icon className="w-7 h-7 text-brand group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" />
                </div>
                <h4 className="font-display text-xl font-semibold text-slate-900 mb-3">{feature.title}</h4>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Applications */}
      <Reveal>
        <h3 className="font-display text-3xl font-bold text-slate-900 text-center mt-20 mb-10">Applications</h3>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-8">
        {[
          { icon: Sprout, title: 'Agricultural Biotechnology', items: ['Bt crop development', 'Gene stacking strategies', 'Resistance management programs', 'Novel trait discovery'] },
          { icon: FlaskRound, title: 'Biopesticide Development', items: ['New insecticidal protein discovery', 'Strain improvement', 'Product optimization', 'Regulatory support'] },
          { icon: GraduationCap, title: 'Academic Research', items: ['Cry toxin evolution studies', 'Host–pathogen interactions', 'Functional genomics', 'Comparative genomics'] },
          { icon: Factory, title: 'Industrial Microbiology', items: ['Strain screening', 'Genome mining', 'Product development', 'Intellectual property generation'] },
        ].map((app, index) => {
          const Icon = app.icon;
          return (
            <Reveal key={index} delay={index * 90}>
              <div className="h-full bg-white rounded-2xl p-6 border border-slate-200 hover:border-brand hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center gap-3 mb-4">
                  <div className="inline-flex w-11 h-11 rounded-xl items-center justify-center bg-brand-tint">
                    <Icon className="w-6 h-6 text-brand group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <h4 className="font-display text-xl font-semibold text-slate-900">{app.title}</h4>
                </div>
                <div className="space-y-1.5">
                  {app.items.map((it, i) => (
                    <div key={i} className="flex items-center text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-brand mr-2 flex-shrink-0" />
                      {it}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>

      {/* Industries served */}
      <Reveal>
        <div className="mt-16 text-center">
          <h3 className="font-display text-2xl font-bold text-slate-900 mb-6">Industries We Serve</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {['Agricultural Biotechnology', 'Seed Technology', 'Crop Protection', 'Research Institutions', 'Universities', 'Government Research Labs', 'Biopesticide Manufacturers'].map((ind, i) => (
              <span key={i} className="px-4 py-2 rounded-full bg-brand-tint border border-blue-100 text-sm font-medium text-brand">{ind}</span>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Closing tagline */}
      <Reveal>
        <div className="mt-16 bg-brand rounded-3xl p-10 text-center text-white">
          <div className="font-display text-2xl sm:text-3xl font-bold mb-3">Discover. Analyze. Innovate.</div>
          <p className="text-blue-50 text-lg">Mining genomes for the future of sustainable agriculture.</p>
          <p className="text-blue-100 text-sm mt-4">Powered by ONEOMICS Private Limited · Advanced Genomics | Bioinformatics | Agricultural Biotechnology | AI-Driven Discovery</p>
        </div>
      </Reveal>
    </div>
  </div>
);

export default AboutPage;